import { useNavigate } from 'react-router-dom'

import { useDashboardPermissions } from '@components'
import { useModalContext } from '@fourthwall/components'
import { Offer } from '@fourthwall/services/dist/models/offer'
import { useLocalStorage } from '@fourthwall/utils/dist/LocalStorage'
import { useWebViewBridge } from '@fourthwall/webview-bridge'
import { routing } from '@utils/routing'

import type { SamplesOrderLocationState } from '../../../../pages/Settings/pages/Samples/pages/SamplesOrder'
import { useOffer } from '../../../hooks/useOffer'
import { OrderSamplesVariantSelectModal } from '../../components/OrderSamplesVariantSelectModal'
import { getSelectOptionsFromVariants } from './utils'

export const useOrderSamples = (offerId: string) => {
  const { open } = useModalContext()
  const { hasPermission } = useDashboardPermissions()
  const navigate = useNavigate()
  const { methods, isWebView } = useWebViewBridge()
  const { localStorage } = useLocalStorage()
  const { offer, isLoading: isLoadingOffer } = useOffer(offerId)
  const areAnyItemsInStock = offer?.variants.some((variant) => variant.quantity !== 0)
  const enabled =
    !isLoadingOffer &&
    Offer.FULFILLING_SERVICES_WITH_SAMPLES.includes(offer.fulfillmentService) &&
    offer.state?.status &&
    Offer.OFFER_STATUSES_WITH_SAMPLES.includes(offer.state?.status) &&
    areAnyItemsInStock &&
    hasPermission('settings.samples.create') &&
    offer.offerType !== 'MOQ'

  const redirectToSamplesOrder = async (variantId: string) => {
    const sampleProduct = { variantId, quantity: 1 }

    if (isWebView) {
      localStorage.set('samples.items', [sampleProduct])
      await methods.orderSampleForProduct?.(sampleProduct)
    } else {
      navigate(routing.settings.samples.order, {
        state: {
          cart: { items: [sampleProduct] },
        } satisfies SamplesOrderLocationState,
      })
    }
  }

  const handleOrderSamples = () => {
    if (isLoadingOffer) return

    if (offer.variants.length === 1) {
      return redirectToSamplesOrder(offer.variants[0].id)
    }

    open(OrderSamplesVariantSelectModal, {
      options: getSelectOptionsFromVariants(offer.variants),
      onConfirm: redirectToSamplesOrder,
    })
  }

  return {
    onOrderSamples: enabled ? handleOrderSamples : undefined,
  }
}
